import React, { createContext, useContext, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import axios from 'axios';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Suppliers from './pages/Suppliers';
import Customers from './pages/Customers';
import SalesAgents from './pages/SalesAgents';
import Purchases from './pages/Purchases';
import Production from './pages/Production';
import ProductionDetail from './pages/ProductionDetail';
import PackagingTeamStatement from './pages/PackagingTeamStatement';
import Orders from './pages/Orders';
import OrderDetail from './pages/OrderDetail';
import OrderEdit from './pages/OrderEdit';  // NEW: Order Edit Page
import BatchInfo from './pages/BatchInfo';  // NEW: Batch Info Page
import Expenses from './pages/Expenses';
import Inventory from './pages/Inventory';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import Users from './pages/Users';
import AuditLog from './pages/AuditLog';
import DailyClose from './pages/DailyClose';
import SupplierStatement from './pages/SupplierStatement';
import AgentStatement from './pages/AgentStatement';
import ProductionCosting from './pages/ProductionCosting';
import BatchReport from './pages/BatchReport';
import CommodityInventory from './pages/CommodityInventory';
import CommodityStatement from './pages/CommodityStatement';

// Layout
import Layout from './components/Layout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const api = axios.create({
  baseURL: API,
});

// Add auth header to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const { user, token } = response.data;
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
    setUser(user);
    return user;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const refreshMe = async () => {
    try {
      const res = await api.get('/auth/me');
      const updated = { ...JSON.parse(localStorage.getItem('user') || '{}'), ...res.data };
      localStorage.setItem('user', JSON.stringify(updated));
      setUser(updated);
    } catch { /* ignore */ }
  };

  const hasModule = (moduleKey) => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    return (user.module_permissions || []).includes(moduleKey);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9FAF9]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1B4D3E]"></div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, login, logout, refreshMe, hasModule, isAdmin: user?.role === 'admin' }}>
      {children}
    </AuthContext.Provider>
  );
}

function ProtectedRoute({ children, module }) {
  const { user, hasModule } = useAuth();
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  // If a module is specified and user doesn't have access, redirect to first available module or login
  if (module && !hasModule(module)) {
    // Find first allowed module to redirect to (avoid infinite loop by checking dashboard first)
    const perms = user.module_permissions || [];
    if (user.role === 'admin' || perms.includes('dashboard')) {
      return <Navigate to="/" replace />;
    }
    // Find first module they can access
    const moduleToPathMap = {
      suppliers: '/suppliers', customers: '/customers', agents: '/agents',
      purchases: '/purchases', production: '/production', orders: '/orders',
      expenses: '/expenses', inventory: '/inventory', daily_close: '/daily-close',
      production_costing: '/production-costing', reports: '/reports', settings: '/settings',
      users: '/users', audit_log: '/audit-log'
    };
    for (const p of perms) {
      if (moduleToPathMap[p]) return <Navigate to={moduleToPathMap[p]} replace />;
    }
    // No modules at all — show no-access page (logout option only)
    return <Layout><NoAccess /></Layout>;
  }
  return <Layout>{children}</Layout>;
}

function NoAccess() {
  const { logout } = useAuth();
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] text-center" data-testid="no-access-page">
      <h2 className="text-2xl font-heading font-bold text-[#1B4D3E] mb-2">No module access</h2>
      <p className="text-slate-500 mb-6 max-w-md">Your account does not have access to any module. Please contact an administrator to grant module permissions.</p>
      <button onClick={() => { logout(); window.location.href = '/login'; }} className="btn-primary px-4 py-2 rounded">Logout</button>
    </div>
  );
}

function AdminRoute({ children }) {
  const { user, isAdmin } = useAuth();
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }
  return <Layout>{children}</Layout>;
}

function App() {
  useEffect(() => {
    // Initialize system on first load
    api.post('/init').catch(() => {});
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/" element={<ProtectedRoute module="dashboard"><Dashboard /></ProtectedRoute>} />
          <Route path="/suppliers" element={<ProtectedRoute module="suppliers"><Suppliers /></ProtectedRoute>} />
          <Route path="/suppliers/:id/statement" element={<ProtectedRoute module="suppliers"><SupplierStatement /></ProtectedRoute>} />
          <Route path="/customers" element={<ProtectedRoute module="customers"><Customers /></ProtectedRoute>} />
          <Route path="/agents" element={<ProtectedRoute module="agents"><SalesAgents /></ProtectedRoute>} />
          <Route path="/agents/:id/statement" element={<ProtectedRoute module="agents"><AgentStatement /></ProtectedRoute>} />
          <Route path="/purchases" element={<ProtectedRoute module="purchases"><Purchases /></ProtectedRoute>} />
          <Route path="/batches/:purchaseId" element={<ProtectedRoute module="reports"><BatchReport /></ProtectedRoute>} />
          <Route path="/commodity-inventory" element={<ProtectedRoute module="inventory"><CommodityInventory /></ProtectedRoute>} />
          <Route path="/commodity/:commodityId/statement" element={<ProtectedRoute module="inventory"><CommodityStatement /></ProtectedRoute>} />
          <Route path="/production" element={<ProtectedRoute module="production"><Production /></ProtectedRoute>} />
          <Route path="/production/:id" element={<ProtectedRoute module="production"><ProductionDetail /></ProtectedRoute>} />
          <Route path="/packaging-teams/:id/statement" element={<ProtectedRoute module="production"><PackagingTeamStatement /></ProtectedRoute>} />
          <Route path="/orders" element={<ProtectedRoute module="orders"><Orders /></ProtectedRoute>} />
          <Route path="/orders/:id" element={<ProtectedRoute module="orders"><OrderDetail /></ProtectedRoute>} />
          <Route path="/orders/:id/edit" element={<ProtectedRoute module="orders"><OrderEdit /></ProtectedRoute>} />
          <Route path="/batch/:batchNo" element={<ProtectedRoute module="inventory"><BatchInfo /></ProtectedRoute>} />
          <Route path="/expenses" element={<ProtectedRoute module="expenses"><Expenses /></ProtectedRoute>} />
          <Route path="/inventory" element={<ProtectedRoute module="inventory"><Inventory /></ProtectedRoute>} />
          <Route path="/daily-close" element={<ProtectedRoute module="daily_close"><DailyClose /></ProtectedRoute>} />
          <Route path="/production-costing" element={<ProtectedRoute module="production_costing"><ProductionCosting /></ProtectedRoute>} />
          <Route path="/reports" element={<ProtectedRoute module="reports"><Reports /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute module="settings"><Settings /></ProtectedRoute>} />
          <Route path="/users" element={<AdminRoute><Users /></AdminRoute>} />
          <Route path="/audit-log" element={<AdminRoute><AuditLog /></AdminRoute>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
